import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileTextIcon } from '../components/icons.js';
import { VersionBadge } from '../components/VersionBadge.js';
import {
  CANONICAL_DOCUMENT_TYPES,
  type DocumentRegistryEntry,
  type Specialisation,
} from '@kennisbank/shared';
import { ApiError, apiGet, apiPatch } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Layout } from '../components/Layout.js';
import { SortableTh, toggleSort } from '../components/SortableTh.js';
import { ChangeRequestLink } from '../components/ChangeRequestButton.js';
import { InlineSpecialisationsCell } from '../components/InlineSpecialisationsCell.js';
import {
  AccessGroupsCell,
  ClassificationCell,
  PiiFlagsCell,
} from '../components/InlineClassificationCell.js';
import {
  DocumentsFilterRail,
  type ClassificationKey,
  type RingKey,
} from '../components/DocumentsFilterRail.js';
import {
  BulkActionBar,
  BulkDeleteModal,
  BulkReclassifyModal,
  exportDocumentsCsv,
} from '../components/DocumentsBulkActions.js';
import type { BulkResult } from '../lib/documents-bulk.js';
import type { Classification, PiiFlag } from '@kennisbank/shared';

/**
 * Browse surface for the OneNebula Knowledgebase corpus — OneView two-
 * column layout: sticky 260px filter rail + dense table.
 *
 * RBAC: open to every authenticated role. Admin (`kb_admin`) gets
 * click-to-edit cells on author/type/specialisations/classification plus
 * a row-selection column for bulk re-classify / export / delete. Other
 * roles never see the selection column — they have no bulk actions.
 *
 * Filter pipeline:
 *   - Server filters: q, specialisation, author, documentType (existing).
 *   - Client-side filters on the returned page: ring, classification,
 *     source type. These were intentionally NOT pushed into the server
 *     query in this slice to keep the data-fetch layer untouched (Phase 3
 *     scope is render-only). Server pagination still applies — clearing
 *     server-side filters first will widen the candidate set.
 */

type SortColumn = 'title' | 'author' | 'documentType' | 'lastIngestedAt' | 'documentDate';

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];
const DEFAULT_PAGE_SIZE: PageSize = 50;

interface ListResponse {
  documents: DocumentRegistryEntry[];
  total: number;
  page: number;
  pageSize: number;
  facets: { authors: { name: string; count: number }[]; documentTypes: string[] };
}

export function DocumentsPage(): JSX.Element {
  const [documents, setDocuments] = useState<DocumentRegistryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [facets, setFacets] = useState<{
    authors: { name: string; count: number }[];
    documentTypes: string[];
  }>({
    authors: [],
    documentTypes: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [selectedSpecs, setSelectedSpecs] = useState<Set<string>>(new Set());
  const [authorFilter, setAuthorFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortColumn>('title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // New client-side dimensions (Phase 3). Empty set = "no filter" (show all).
  const [ringFilter, setRingFilter] = useState<Set<RingKey>>(new Set());
  const [classificationFilter, setClassificationFilter] = useState<Set<ClassificationKey>>(
    new Set(),
  );
  // Bulk-selection state — set of filenames.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Rows are snapshotted when a bulk dialog opens so its title and list
  // don't shift while the page refetches underneath it.
  const [bulkDialog, setBulkDialog] = useState<{
    kind: 'reclassify' | 'delete';
    docs: DocumentRegistryEntry[];
  } | null>(null);
  // Bumped after a bulk run to refetch the current page from the server.
  const [reloadKey, setReloadKey] = useState(0);

  const { user } = useAuth();
  const canEdit = user?.role === 'kb_admin';
  const [savingFilename, setSavingFilename] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [availableAccessGroups, setAvailableAccessGroups] = useState<string[]>([]);
  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    void apiGet<{ accessGroups: string[] }>('/api/v1/admin/knowledgebase/access-groups')
      .then((r) => {
        if (!cancelled) setAvailableAccessGroups(r.accessGroups ?? []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [canEdit]);

  // Live admin-managed specialisation vocabulary. Fetched once on mount;
  // every authenticated role can read this list (GET /admin/specialisations
  // is unauthenticated for writes only). Stale-while-revalidate: the
  // InlineSpecialisationsCell + filter rail fall back to the hardcoded
  // CANONICAL_* / ALL_SPECIALISATIONS arrays until this resolves so the
  // UI stays functional if the request blips.
  const [ring0Names, setRing0Names] = useState<string[] | undefined>(undefined);
  const [ring1Names, setRing1Names] = useState<string[] | undefined>(undefined);
  const [allSpecsByRing, setAllSpecsByRing] = useState<
    { name: string; ring: 'ring0' | 'ring1' }[] | undefined
  >(undefined);
  useEffect(() => {
    let cancelled = false;
    void apiGet<{ specs: Specialisation[] }>('/api/v1/admin/specialisations')
      .then((res) => {
        if (cancelled) return;
        const ring0 = res.specs.filter((s) => s.ring === 'ring0').map((s) => s.name);
        const ring1 = res.specs.filter((s) => s.ring === 'ring1').map((s) => s.name);
        setRing0Names(ring0);
        setRing1Names(ring1);
        // Ring-tagged list for the rail picker (ring badge + section header).
        const tagged: { name: string; ring: 'ring0' | 'ring1' }[] = [
          ...ring0.map((n) => ({ name: n, ring: 'ring0' as const })),
          ...ring1.map((n) => ({ name: n, ring: 'ring1' as const })),
        ];
        setAllSpecsByRing(tagged);
      })
      .catch(() => {
        // Silent: components fall back to the hardcoded vocabulary.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const patchDocument = useCallback(
    async (filename: string, patch: Partial<DocumentRegistryEntry>): Promise<void> => {
      setSavingFilename(filename);
      setSaveError(null);
      try {
        const res = await apiPatch<{ document: DocumentRegistryEntry }>(
          `/api/v1/admin/knowledgebase/documents/${encodeURIComponent(filename)}`,
          patch,
        );
        setDocuments((cur) =>
          cur.map((d) => (d.filename === filename ? { ...d, ...res.document } : d)),
        );
      } catch (err) {
        setSaveError(
          err instanceof ApiError && err.code === 'ingest_lock_held'
            ? 'Indexing is still running. Please try again shortly.'
            : err instanceof Error
              ? err.message
              : 'Update failed.',
        );
      } finally {
        setSavingFilename(null);
      }
    },
    [],
  );

  useEffect(() => {
    setPage(1);
  }, [
    q,
    selectedSpecs,
    authorFilter,
    typeFilter,
    sortBy,
    sortDir,
    pageSize,
    classificationFilter,
    ringFilter,
  ]);

  useEffect(() => {
    let cancelled = false;
    const debounceMs = q.trim().length > 0 ? 250 : 0;
    const timer = setTimeout(() => {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      selectedSpecs.forEach((s) => params.append('specialisation', s));
      if (authorFilter) params.set('author', authorFilter);
      if (typeFilter) params.set('documentType', typeFilter);
      // Server-side classification + ring filtering so the page count is
      // honest. Client-side filter was a no-op against a 6493-doc corpus
      // paginated server-side at 50/page — confidential docs past page 1
      // never showed (Deloitte upload bug, 2026-05-18).
      classificationFilter.forEach((c) => params.append('classification', c));
      ringFilter.forEach((r) => params.append('ring', r));
      params.set('sortBy', sortBy);
      params.set('sortDir', sortDir);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      setLoading(true);
      setError(null);
      apiGet<ListResponse>(`/api/v1/documents?${params.toString()}`)
        .then((res) => {
          if (cancelled) return;
          // A bulk delete can empty the last page; step back to the new last
          // page instead of showing "No documents found" with rows remaining.
          if (res.documents.length === 0 && res.total > 0 && page > 1) {
            setPage(Math.ceil(res.total / pageSize));
            return;
          }
          setDocuments(res.documents);
          setTotal(res.total);
          setFacets(res.facets);
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setError(err instanceof ApiError ? err.message : 'Could not load document list.');
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    q,
    selectedSpecs,
    authorFilter,
    typeFilter,
    sortBy,
    sortDir,
    page,
    pageSize,
    classificationFilter,
    ringFilter,
    reloadKey,
  ]);

  // ring + classification filter server-side (see params block above) so
  // the page count is honest. Source-type filter was dropped — redundant
  // with documentType (Source Code) and the filename column.
  const visibleDocuments = documents;

  // Prune selection of any filename that's no longer visible after a
  // filter/page change so the bulk bar count never lies.
  useEffect(() => {
    setSelectedIds((cur) => {
      if (cur.size === 0) return cur;
      const visible = new Set(visibleDocuments.map((d) => d.filename));
      let changed = false;
      const next = new Set<string>();
      cur.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : cur;
    });
  }, [visibleDocuments]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(page * pageSize, total);

  const toggleSpec = (s: string): void => {
    setSelectedSpecs((cur) => {
      const next = new Set(cur);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const toggleRing = (r: RingKey): void => {
    setRingFilter((cur) => {
      const next = new Set(cur);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  };
  const toggleClassification = (c: ClassificationKey): void => {
    setClassificationFilter((cur) => {
      const next = new Set(cur);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };
  const clearFilters = (): void => {
    setQ('');
    setSelectedSpecs(new Set());
    setAuthorFilter('');
    setTypeFilter('');
    setRingFilter(new Set());
    setClassificationFilter(new Set());
  };

  const hasActiveFilters =
    q.length > 0 ||
    selectedSpecs.size > 0 ||
    authorFilter.length > 0 ||
    typeFilter.length > 0 ||
    ringFilter.size > 0 ||
    classificationFilter.size > 0;

  // Row-selection helpers.
  const toggleRow = (filename: string): void => {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(filename)) next.delete(filename);
      else next.add(filename);
      return next;
    });
  };
  const allVisibleSelected =
    visibleDocuments.length > 0 && visibleDocuments.every((d) => selectedIds.has(d.filename));
  const someVisibleSelected =
    !allVisibleSelected && visibleDocuments.some((d) => selectedIds.has(d.filename));
  const toggleAllVisible = (): void => {
    setSelectedIds((cur) => {
      if (allVisibleSelected) {
        const next = new Set(cur);
        visibleDocuments.forEach((d) => next.delete(d.filename));
        return next;
      }
      const next = new Set(cur);
      visibleDocuments.forEach((d) => next.add(d.filename));
      return next;
    });
  };
  const clearSelection = (): void => setSelectedIds(new Set());
  const selectedDocs = (): DocumentRegistryEntry[] =>
    visibleDocuments.filter((d) => selectedIds.has(d.filename));
  const onBulkFinished = (result: BulkResult<DocumentRegistryEntry>): void => {
    // Failed rows stay selected so the admin can retry just those.
    setSelectedIds(new Set(result.failed.map((f) => f.item.filename)));
    if (result.succeeded.length > 0) setReloadKey((k) => k + 1);
  };

  // Title, Author, Type, Specialisations, Date, Updated (+ selection and
  // Classification for admins).
  const colSpan = canEdit ? 8 : 6;

  return (
    <Layout>
      <div className="flex h-full min-h-0">
        <DocumentsFilterRail
          q={q}
          onQChange={setQ}
          authors={facets.authors}
          authorFilter={authorFilter}
          onAuthorChange={setAuthorFilter}
          documentTypes={[...CANONICAL_DOCUMENT_TYPES].sort()}
          typeFilter={typeFilter}
          onTypeChange={setTypeFilter}
          ringFilter={ringFilter}
          onToggleRing={toggleRing}
          classificationFilter={classificationFilter}
          onToggleClassification={toggleClassification}
          selectedSpecs={selectedSpecs}
          onToggleSpec={toggleSpec}
          availableSpecialisationsByRing={allSpecsByRing}
          hasActiveFilters={hasActiveFilters}
          onClear={clearFilters}
        />

        <div className="thin-scroll relative min-w-0 flex-1 overflow-y-auto">
          <div className="px-6 py-5">
            <header className="mb-4">
              <h1 className="ov-h-page">Documents</h1>
              <p className="ov-meta mt-1">
                Browse the OneNebula Knowledgebase by theme, author, or title. Click a title to open
                the source file.
              </p>
            </header>

            {error && (
              <p className="mb-3 rounded-md bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
                {error}
              </p>
            )}
            {saveError && (
              <p className="mb-3 rounded-md bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
                {saveError}
              </p>
            )}

            {/* Mobile stacked list — kept minimal; rail is hidden below md
                via the parent flex collapsing on small screens is a follow-up.
                For Phase 3, mobile users see the unfiltered table below. */}

            {/*
              `table-fixed` honours <col> widths regardless of cell content.
              With the filter rail eating ~260px of content, the previous
              widths left Title at ~130px, jamming the Title sort caret
              against the Author header. Tightened the fixed columns so
              Title gets ~270px+ even with the rail open.
            */}
            <div className="overflow-x-auto rounded-md border border-nebula-border-soft bg-nebula-surface">
              <table className="w-full min-w-[820px] table-fixed text-sm">
                <colgroup>
                  {canEdit && <col className="w-10" />}
                  <col />
                  <col className="w-32" />
                  <col className="w-24" />
                  <col className="w-44" />
                  {canEdit && <col className="w-32" />}
                  <col className="w-24" />
                  <col className="w-28" />
                </colgroup>
                <thead className="border-b border-nebula-border bg-nebula-gray-6">
                  <tr>
                    {canEdit && (
                      <th className="px-3 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someVisibleSelected;
                          }}
                          onChange={toggleAllVisible}
                          aria-label="Select all visible rows"
                          className="h-3.5 w-3.5 cursor-pointer rounded border-nebula-border accent-nebula-primary"
                        />
                      </th>
                    )}
                    <SortableTh<SortColumn>
                      label="Title"
                      column="title"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                      className="ov-meta-upper px-3 py-2 text-left"
                    />
                    <SortableTh<SortColumn>
                      label="Author"
                      column="author"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                      className="ov-meta-upper px-3 py-2 text-left"
                    />
                    <SortableTh<SortColumn>
                      label="Type"
                      column="documentType"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                      className="ov-meta-upper px-3 py-2 text-left"
                    />
                    <th className="ov-meta-upper px-3 py-2 text-left">Specialisations</th>
                    {canEdit && (
                      <th className="ov-meta-upper px-3 py-2 text-left">Classification</th>
                    )}
                    <SortableTh<SortColumn>
                      label="Date"
                      column="documentDate"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                      className="ov-meta-upper px-3 py-2 text-left"
                    />
                    <SortableTh<SortColumn>
                      label="Updated"
                      column="lastIngestedAt"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                      className="ov-meta-upper px-3 py-2 text-left"
                    />
                  </tr>
                </thead>
                <tbody>
                  {visibleDocuments.map((d) => {
                    const isSelected = selectedIds.has(d.filename);
                    return (
                      <tr
                        key={d.filename}
                        className={`border-b border-nebula-border-soft align-top transition-colors ${
                          isSelected ? 'bg-nebula-tint' : 'hover:bg-nebula-tint'
                        }`}
                      >
                        {canEdit && (
                          <td className="px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRow(d.filename)}
                              aria-label={`Select ${d.documentTitle || d.filename}`}
                              className="h-3.5 w-3.5 cursor-pointer rounded border-nebula-border accent-nebula-primary"
                            />
                          </td>
                        )}
                        <td className="overflow-hidden px-3 py-2 align-top font-medium text-nebula-fg-1">
                          <div className="flex min-w-0 items-start gap-2">
                            <button
                              type="button"
                              onClick={() => void openDocument(d, setError)}
                              title={d.documentTitle || d.filename}
                              className="inline-flex min-w-0 items-start gap-2 text-left text-nebula-fg-1 hover:text-nebula-secondary hover:underline"
                            >
                              <FileTextIcon
                                size={14}
                                className="mt-0.5 flex-shrink-0 text-nebula-fg-4"
                                aria-hidden="true"
                              />
                              <span className="min-w-0 break-all">
                                {d.documentTitle || d.filename}
                              </span>
                            </button>
                            {d.ring === 'ring0' && (
                              <span
                                title="OneNebula internal document — visible only to OneNebula employees."
                                className="mt-0.5 inline-flex flex-shrink-0 items-center rounded-md bg-nebula-gray-5 px-1.5 py-0.5 text-[10px] font-medium text-nebula-fg-1"
                              >
                                Internal
                              </span>
                            )}
                            {d.documentOnly && (
                              <span
                                title="Available as download only — the assistant does not search this document."
                                className="mt-0.5 inline-flex flex-shrink-0 items-center rounded-md bg-nebula-warning-bg px-1.5 py-0.5 text-[10px] font-medium text-nebula-warning"
                              >
                                Download only
                              </span>
                            )}
                            <VersionBadge doc={d} />
                          </div>
                        </td>
                        <td className="overflow-hidden truncate px-3 py-2 align-top text-nebula-fg-2">
                          {canEdit ? (
                            <InlineAuthorCell
                              value={d.author}
                              saving={savingFilename === d.filename}
                              onSave={(v) => patchDocument(d.filename, { author: v })}
                            />
                          ) : (
                            d.author || <span className="text-nebula-fg-4">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top text-nebula-fg-2">
                          {canEdit ? (
                            <InlineDocumentTypeCell
                              value={d.documentType ?? ''}
                              saving={savingFilename === d.filename}
                              onSave={(v) =>
                                patchDocument(d.filename, {
                                  documentType: v === '' ? null : v,
                                } as Partial<DocumentRegistryEntry>)
                              }
                            />
                          ) : (
                            d.documentType || <span className="text-nebula-fg-4">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {canEdit ? (
                            <InlineSpecialisationsCell
                              value={d.specialisations ?? []}
                              saving={savingFilename === d.filename}
                              onSave={(v) => patchDocument(d.filename, { specialisations: v })}
                              maxItems={10}
                              ring={d.ring}
                              availableNames={d.ring === 'ring0' ? ring0Names : ring1Names}
                            />
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(d.specialisations ?? []).slice(0, 3).map((s) => (
                                <span
                                  key={s}
                                  className="rounded-full bg-nebula-tint px-2 py-0.5 text-[11px] font-medium text-nebula-secondary"
                                >
                                  {s}
                                </span>
                              ))}
                              {(d.specialisations?.length ?? 0) > 3 && (
                                <span
                                  title={(d.specialisations ?? []).slice(3).join(', ')}
                                  className="rounded-full bg-nebula-gray-5 px-2 py-0.5 text-[11px] font-medium text-nebula-fg-2"
                                >
                                  +{(d.specialisations?.length ?? 0) - 3} more
                                </span>
                              )}
                              {(!d.specialisations || d.specialisations.length === 0) && (
                                <span className="text-nebula-fg-4">—</span>
                              )}
                            </div>
                          )}
                        </td>
                        {canEdit && (
                          <td className="px-3 py-2 align-top">
                            <div className="flex flex-col gap-1">
                              <ClassificationCell
                                value={(d.classification as Classification) ?? 'internal'}
                                saving={savingFilename === d.filename}
                                onSave={(v) =>
                                  patchDocument(d.filename, {
                                    classification: v,
                                  } as Partial<DocumentRegistryEntry>)
                                }
                              />
                              <AccessGroupsCell
                                value={(d as { accessGroups?: string[] }).accessGroups ?? []}
                                available={availableAccessGroups}
                                saving={savingFilename === d.filename}
                                onSave={(v) =>
                                  patchDocument(d.filename, {
                                    accessGroups: v,
                                  } as Partial<DocumentRegistryEntry>)
                                }
                              />
                              <PiiFlagsCell
                                value={
                                  ((d as { piiFlags?: PiiFlag[] }).piiFlags ?? []) as PiiFlag[]
                                }
                                saving={savingFilename === d.filename}
                                onSave={(v) =>
                                  patchDocument(d.filename, {
                                    piiFlags: v,
                                  } as Partial<DocumentRegistryEntry>)
                                }
                              />
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-2 align-top text-nebula-fg-3">
                          <span
                            className="ov-numeric text-[12px]"
                            title="Document date (authored / published). Distinct from when it was indexed."
                          >
                            {d.documentDate ? formatDate(d.documentDate) : '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top text-nebula-fg-3">
                          <div className="flex flex-col gap-0.5">
                            <span className="ov-numeric text-[12px]">
                              {d.lastIngestedAt ? formatDate(d.lastIngestedAt) : '—'}
                            </span>
                            <ChangeRequestLink
                              context={{
                                surface: 'documenten',
                                filename: d.filename,
                                documentTitle: d.documentTitle ?? d.filename,
                                author: d.author ?? '',
                                specialisations: d.specialisations ?? [],
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {loading && visibleDocuments.length === 0 && (
                    <tr>
                      <td colSpan={colSpan} className="px-3 py-10 text-center">
                        <div className="ov-pulse ov-meta">Loading documents…</div>
                      </td>
                    </tr>
                  )}
                  {!loading && visibleDocuments.length === 0 && (
                    <tr>
                      <td colSpan={colSpan} className="px-3 py-12 text-center">
                        <p className="ov-body text-nebula-fg-3">
                          No documents found for these filters.{' '}
                          {hasActiveFilters && (
                            <button
                              type="button"
                              onClick={clearFilters}
                              className="text-nebula-secondary hover:underline"
                            >
                              Clear filters
                            </button>
                          )}
                        </p>
                        <p className="ov-meta mt-2">
                          Or ask the{' '}
                          <Link
                            to={`/agent${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`}
                            className="text-nebula-secondary hover:underline"
                          >
                            Knowledge Agent
                          </Link>
                          .
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex items-center justify-between text-[11px] text-nebula-fg-3">
              <span className="ov-numeric">
                {loading && total === 0 ? (
                  ''
                ) : total === 0 ? (
                  'No documents'
                ) : (
                  <>
                    {pageStart}–{pageEnd} of {total}
                    {hasActiveFilters ? ' (filtered)' : ''}
                  </>
                )}
              </span>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1 text-xs">
                  <span className="text-nebula-fg-3">Per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value) as PageSize)}
                    disabled={loading}
                    className="rounded-md border border-nebula-border bg-nebula-surface px-1.5 py-0.5 text-xs text-nebula-fg-2 focus:border-nebula-secondary focus:outline-none disabled:opacity-40"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || loading}
                      className="rounded-md border border-nebula-border bg-nebula-surface px-2 py-1 text-xs text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-40"
                    >
                      ‹ Previous
                    </button>
                    <span className="ov-numeric text-xs">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages || loading}
                      className="rounded-md border border-nebula-border bg-nebula-surface px-2 py-1 text-xs text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-40"
                    >
                      Next ›
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sticky bulk-action bar — only over the table column, not the
              rail (the parent flex slot starts to the right of the rail). */}
          {canEdit && selectedIds.size > 0 && (
            <BulkActionBar
              count={selectedIds.size}
              onClear={clearSelection}
              onReclassify={() => setBulkDialog({ kind: 'reclassify', docs: selectedDocs() })}
              onDelete={() => setBulkDialog({ kind: 'delete', docs: selectedDocs() })}
              onExport={() => exportDocumentsCsv(selectedDocs())}
            />
          )}
        </div>
      </div>
      {bulkDialog?.kind === 'reclassify' && (
        <BulkReclassifyModal
          docs={bulkDialog.docs}
          availableAccessGroups={availableAccessGroups}
          onClose={() => setBulkDialog(null)}
          onFinished={onBulkFinished}
        />
      )}
      {bulkDialog?.kind === 'delete' && (
        <BulkDeleteModal
          docs={bulkDialog.docs}
          onClose={() => setBulkDialog(null)}
          onFinished={onBulkFinished}
        />
      )}
    </Layout>
  );
}

/**
 * Confluence-ingested docs (`confluence-{pageId}-{slug}.md`) don't have a
 * "downloadable" canonical file — the user expects to land on the actual
 * Confluence page. Extract the pageId from the filename and open
 * viewpage.action in a new tab. Returns null when the filename doesn't
 * match the confluence pattern.
 */
function extractConfluencePageId(filename: string): string | null {
  const m = /^confluence-(\d+)-/.exec(filename);
  return m ? (m[1] ?? null) : null;
}

const CONFLUENCE_BASE_HOST = 'onenebula.atlassian.net';

async function openDocument(
  doc: DocumentRegistryEntry,
  setError: (s: string | null) => void,
): Promise<void> {
  const pageId = extractConfluencePageId(doc.filename);
  if (pageId) {
    const url = `https://${CONFLUENCE_BASE_HOST}/wiki/pages/viewpage.action?pageId=${pageId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  try {
    const res = await apiGet<{ url: string; expiresAt: string }>(
      `/api/v1/documents/${encodeURIComponent(doc.filename)}/download-url`,
    );
    window.open(res.url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    if (err instanceof ApiError && err.code === 'source_not_in_bucket') {
      setError(
        `Source file "${doc.filename}" is no longer in the bucket. This happens for older documents.`,
      );
      return;
    }
    setError(err instanceof ApiError ? err.message : 'Could not open source file.');
  }
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ----- Inline editing cells (admin-only) ------------------------------------

function InlineAuthorCell({
  value,
  saving,
  onSave,
}: {
  value: string;
  saving: boolean;
  onSave: (value: string) => void | Promise<void>;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [editing, value]);

  const commit = async (): Promise<void> => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      setEditing(false);
      return;
    }
    await onSave(trimmed);
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Click to edit the author"
        className="w-full break-all rounded-md px-1.5 py-0.5 text-left hover:bg-nebula-tint"
      >
        {value || <span className="text-nebula-fg-4">—</span>}
      </button>
    );
  }
  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void commit();
        } else if (e.key === 'Escape') {
          setEditing(false);
        }
      }}
      aria-label="Edit author"
      className="w-full rounded-md border border-nebula-border bg-nebula-surface px-2 py-0.5 text-sm text-nebula-fg-1 outline-none focus:border-nebula-secondary"
    />
  );
}

function InlineDocumentTypeCell({
  value,
  saving,
  onSave,
}: {
  value: string;
  saving: boolean;
  onSave: (next: string) => void | Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const choose = async (next: string): Promise<void> => {
    setOpen(false);
    if (next === value) return;
    await onSave(next);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        title="Click to edit document type"
        className="w-full rounded-md px-1.5 py-0.5 text-left hover:bg-nebula-tint disabled:opacity-50"
      >
        {value || <span className="text-nebula-fg-4">—</span>}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 max-h-60 w-44 overflow-y-auto rounded-md border border-nebula-border bg-nebula-surface p-1 shadow-lg">
          {CANONICAL_DOCUMENT_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => void choose(t)}
              className={`block w-full rounded px-2 py-1 text-left text-xs hover:bg-nebula-tint ${
                t === value
                  ? 'bg-nebula-tint font-medium text-nebula-secondary'
                  : 'text-nebula-fg-1'
              }`}
            >
              {t}
            </button>
          ))}
          {value && (
            <>
              <div className="my-1 border-t border-nebula-border-soft" />
              <button
                type="button"
                onClick={() => void choose('')}
                className="block w-full rounded px-2 py-1 text-left text-xs text-nebula-fg-2 hover:bg-nebula-error-bg hover:text-nebula-error"
              >
                Clear
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
